import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import {
    faPhone,
    faMobileScreen,
    faChevronDown,
    faBars,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import logo from './assets/images/logo.png';
import { colleges } from './data/colleges';
import { useContent } from './data/content';
import './Header.css';

/* "Colleges" is the one nav item that opens rather than navigates — the four
   colleges are the site's spine, so they get named in the menu instead of
   hiding behind a single "Courses" link. */
const NAV = [
    { label: 'Home', href: '/home' },
    { label: 'Colleges', colleges: true },
    { label: 'About', href: '/about-us' },
    { label: 'Facilities', href: '/facilities' },
    { label: 'Activities', href: '/home#activities' },
    { label: 'Careers', href: '/careers' },
    { label: 'Contact', href: '#site-footer' },
];

const PHONES = [
    { icon: faMobileScreen, label: '+91-9899923387', href: 'tel:+919899923387' },
];

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [phoneOpen, setPhoneOpen] = useState(false);
    const [collegeOpen, setCollegeOpen] = useState(false);
    const phoneRef = useRef(null);
    const collegeRef = useRef(null);

    // CMS custom pages flagged for the nav (none by default → nav unchanged)
    const { content } = useContent();
    const customPages = Array.isArray(content?.pages)
        ? content.pages.filter((p) => p.showInNav !== false)
        : [];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (phoneRef.current && !phoneRef.current.contains(e.target)) {
                setPhoneOpen(false);
            }
            if (collegeRef.current && !collegeRef.current.contains(e.target)) {
                setCollegeOpen(false);
            }
        };
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    return (
        <header className={`nav ${scrolled ? 'nav--solid' : ''}`}>
            <div className="container-wide nav-inner">
                <a href="/home" className="nav-brand" onClick={() => setMenuOpen(false)}>
                    <img src={logo} alt="" className="nav-logo" />
                    <span className="nav-brand-text">
                        <span className="nav-brand-name seal-type">Sri Guru Arjun Dev Ji</span>
                        <span className="nav-brand-sub">Group of Institutes</span>
                    </span>
                </a>

                <nav className="nav-links" aria-label="Primary">
                    {NAV.map((item) =>
                        item.colleges ? (
                            <div className="nav-colleges" key={item.label} ref={collegeRef}>
                                <button
                                    type="button"
                                    className="nav-link nav-colleges-btn"
                                    aria-expanded={collegeOpen}
                                    onClick={() => setCollegeOpen((v) => !v)}
                                >
                                    {item.label}
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                        className={`nav-colleges-chev ${collegeOpen ? 'is-open' : ''}`}
                                    />
                                </button>
                                {collegeOpen && (
                                    <div className="nav-colleges-menu">
                                        {colleges.map((c) => (
                                            <a
                                                key={c.slug}
                                                href={`/colleges/${c.slug}`}
                                                className="nav-college-item"
                                                style={{ '--accent': `var(--v-${c.slug})` }}
                                            >
                                                <span className="nav-college-rule" aria-hidden="true" />
                                                <span>
                                                    <span className="nav-college-name seal-type">{c.name}</span>
                                                    <span className="nav-college-sub">{c.tagline}</span>
                                                </span>
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <a key={item.label} href={item.href} className="nav-link">
                                {item.label}
                            </a>
                        )
                    )}
                    {customPages.map((p) => (
                        <a key={p.slug} href={`/p/${p.slug}`} className="nav-link">
                            {p.title}
                        </a>
                    ))}
                </nav>

                <div className="nav-actions">
                    <div className="nav-phone" ref={phoneRef}>
                        <button
                            type="button"
                            className="nav-phone-btn"
                            aria-expanded={phoneOpen}
                            onClick={() => setPhoneOpen((v) => !v)}
                        >
                            <FontAwesomeIcon icon={faPhone} />
                            <span>Call us</span>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`nav-phone-chev ${phoneOpen ? 'is-open' : ''}`}
                            />
                        </button>
                        {phoneOpen && (
                            <div className="nav-phone-menu">
                                {PHONES.map((p) => (
                                    <a key={p.href} href={p.href} className="nav-phone-item">
                                        <FontAwesomeIcon icon={p.icon} />
                                        {p.label}
                                    </a>
                                ))}
                                <div className="nav-phone-socials">
                                    <a target="_blank" rel="noreferrer" aria-label="Facebook" href="https://www.facebook.com/profile.php?id=61581619677373">
                                        <FontAwesomeIcon icon={faFacebook} />
                                    </a>
                                    <a target="_blank" rel="noreferrer" aria-label="Instagram" href="https://www.instagram.com/guruarjundevgroupofinstitutes/">
                                        <FontAwesomeIcon icon={faInstagram} />
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>

                    <a href="/admissions" className="btn btn--primary nav-apply">
                        Inquiry now
                    </a>

                    <button
                        type="button"
                        className="nav-burger"
                        aria-label="Open menu"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(true)}
                    >
                        <FontAwesomeIcon icon={faBars} />
                    </button>
                </div>
            </div>

            {/* Mobile drawer — wrapped in a viewport-clipped overlay so the
                off-screen (translated) panel never widens the document */}
            <div className={`nav-overlay ${menuOpen ? 'is-open' : ''}`}>
              <div className="nav-scrim" onClick={() => setMenuOpen(false)} />
              <aside className={`nav-drawer ${menuOpen ? 'is-open' : ''}`}>
                <div className="nav-drawer-top">
                    <span className="nav-brand-name">Menu</span>
                    <button
                        type="button"
                        className="nav-burger"
                        aria-label="Close menu"
                        onClick={() => setMenuOpen(false)}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
                <nav className="nav-drawer-links" aria-label="Mobile">
                    {NAV.map((item) =>
                        item.colleges ? (
                            <div className="nav-drawer-group" key={item.label}>
                                <span className="nav-drawer-grouptitle">Colleges</span>
                                {colleges.map((c) => (
                                    <a
                                        key={c.slug}
                                        href={`/colleges/${c.slug}`}
                                        className="nav-drawer-link nav-drawer-link--college seal-type"
                                        style={{ '--accent': `var(--v-${c.slug})` }}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        {c.name}
                                    </a>
                                ))}
                            </div>
                        ) : (
                            <a
                                key={item.label}
                                href={item.href}
                                className="nav-drawer-link"
                                onClick={() => setMenuOpen(false)}
                            >
                                {item.label}
                            </a>
                        )
                    )}
                    {customPages.map((p) => (
                        <a
                            key={p.slug}
                            href={`/p/${p.slug}`}
                            className="nav-drawer-link"
                            onClick={() => setMenuOpen(false)}
                        >
                            {p.title}
                        </a>
                    ))}
                </nav>
                <div className="nav-drawer-foot">
                    <a href="/admissions" className="btn btn--primary" onClick={() => setMenuOpen(false)}>
                        Apply now
                    </a>
                    <div className="nav-drawer-calls">
                        {PHONES.map((p) => (
                            <a key={p.href} href={p.href} className="nav-phone-item">
                                <FontAwesomeIcon icon={p.icon} />
                                {p.label}
                            </a>
                        ))}
                    </div>
                </div>
              </aside>
            </div>
        </header>
    );
};

export default Header;
